import { createSubscriptionPaymentOrder, verifyCashfreePayment } from '@/lib/cashfree/paymentService';
import { useToast } from '@/components/ui/use-toast';
import { useState } from 'react';
import { buyTrustPoints } from '@/lib/appwrite/trustScore';
import { useNavigate } from 'react-router-dom';
import { appwriteConfig, databases } from '@/lib/appwrite/config';
import { Query } from 'appwrite';
import { paymentContactFromIdentifier } from '@/lib/cashfree/paymentContact';

// Simple payment creation function for subscriptions
export async function createSubscriptionPayment(
  userId: string, 
  amount: number,
  user: {
    name: string;
    emailOrPhone: string;
    paymentPhone?: string;
  },
  subscriptionType: string,
  points: number
) {
  try {
    const paymentContact = paymentContactFromIdentifier(user.emailOrPhone, user.paymentPhone);
    // Create payment order with Cashfree
    const orderResponse = await createSubscriptionPaymentOrder(
      userId,
      amount,
      points,
      {
        id: userId,
        name: user.name,
        email: paymentContact.email,
        phone: paymentContact.phone
      },
      subscriptionType,
      `${window.location.origin}/payment/callback?source=subscription&entity_id=${userId}&subscription_type=${subscriptionType}`
    );

    if (!orderResponse.success || !orderResponse.payment_session_id) {
      throw new Error(orderResponse.error || 'Failed to create payment session');
    }

    return {
      success: true,
      payment_session_id: orderResponse.payment_session_id,
      order_id: orderResponse.order_id
    };

  } catch (error) {
    console.error('Error creating subscription payment:', error);
    throw error;
  }
}

// Hook for handling subscription payments
export const useSubscriptionPayment = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false); // Added for verification state
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubscriptionPayment = async (
    userId: string,
    amount: number,
    points: number,
    user: {
      name: string;
      emailOrPhone: string;
      paymentPhone?: string;
    },
    subscriptionType: string,
  ) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // Create payment order
      const paymentOrder = await createSubscriptionPayment(
        userId, 
        amount, 
        user, 
        subscriptionType,
        points,
      );
      
      if (!paymentOrder.success) {
        throw new Error('Failed to create payment order');
      }

      // Initialize Cashfree payment
      const cashfree = new window.Cashfree({
        mode: 'production' // or 'sandbox' for testing
      });

      const checkoutOptions = {
        paymentSessionId: paymentOrder.payment_session_id,
        returnUrl: `${window.location.origin}/payment/callback?order_id=${paymentOrder.order_id}&source=subscription&entity_id=${userId}&subscription_type=${subscriptionType}`,
        redirectTarget: '_modal'
      };

      // Handle payment completion
      cashfree.checkout(checkoutOptions).then(async (response: any) => {
        try {
          if (response.error) {
            throw new Error(response.error.message || 'Payment failed');
          }

          if (response.paymentDetails) {
            setIsVerifying(true); // Start verification loading
            
            // Verify payment on our backend
            const isValid = await verifyCashfreePayment({
              orderId: paymentOrder.order_id!,
              paymentId: response.paymentDetails.paymentId
            });

            if (!isValid) {
              throw new Error("Payment verification failed");
            }

            await buyTrustPoints(userId, points, amount);
            
            // Payment successful - show immediate feedback
            toast({
              title: "Payment Successful",
              description: `₹${amount} paid successfully`,
            });
            
            navigate('/trust-score');
            // The webhook will handle the backend processing
          }
        } catch (error) {
          console.error("Payment processing failed:", error);
          
          toast({
            title: "Payment Verification Failed",
            description: "Please contact support if amount was deducted",
            variant: "destructive"
          });
        } finally {
          setIsVerifying(false); // End verification loading
        }
      }).catch((error: any) => {
        console.error("Cashfree checkout error:", error);
        toast({
          title: "Payment Failed",
          description: error.message || "Payment could not be completed",
          variant: "destructive"
        });
      });

    } catch (error) {
      console.error("Payment initiation failed:", error);
      
      toast({
        title: "Payment Failed",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    handleSubscriptionPayment,
    isProcessing,
    isVerifying // Added for verification state
  };
};

export async function getUserActiveSubscription(userId: string) {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.transactionsCollectionId,
      [
        Query.equal('userId', userId),
        Query.equal('status', 'completed'), // Only get completed transactions
        Query.orderDesc('$createdAt'),
        Query.limit(1)
      ]
    );
    
    // If no transactions found, return null
    if (response.documents.length === 0) {
      return null;
    }
    
    // Get the latest subscription
    const subscription = response.documents[0];
    
    // Check if subscription is still active (within 28 days)
    const createdDate = new Date(subscription.createdAt);
    const expiryDate = new Date(createdDate.getTime() + (28 * 24 * 60 * 60 * 1000));
    const now = new Date();
    
    // Only return subscription if it's still active
    if (now < expiryDate) {
      return subscription; 
    }
    
    // Subscription has expired, return null
    return null;
    
  } catch (error) {
    console.error('Error fetching active subscription:', error);
    throw error;
  }
}
